<template>
	<button v-if="showDisclaimer" class="tooltip disclaimer-button" @click.prevent.stop="openDisclaimer">
		<img src="/appassets/img/info-icon-dark.svg" height="16" alt="info" />
	</button>
</template>

<script lang="ts" setup generic="">
import { VFMHelper } from '@/helpers/vfm/vfm-helper';
import { computed, CreateElement, onMounted, RenderContext } from 'vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { PopoverHelper } from '@/helpers/vfm/popover-helper';

const props = withDefaults(defineProps<{ price?: string; disclaimerText?: string }>(), {
	price: '',
});

const { country } = storeToRefs(useLanguageStore());

const acceptedCountries = ['MX', 'DE', 'US', 'CA', 'AU', 'UK', 'GB', 'IE', 'NZ', 'ZA'];

const showDisclaimer = computed(() => acceptedCountries.includes(country.value) || props.disclaimerText != null);

const { t } = useI18n();

onMounted(() => {
	if (process.env.NODE_ENV === 'development' && !props.price && !props.disclaimerText) {
		// unable to enforce this on components with optional prop unions. will reapproach this with Vue 3.4+
		console.error(
			'DisclaimerButton: No price or disclaimerText provided. ATV+ disclaimerText will be rendered without price by default.'
		);
	}
});

async function openDisclaimer(event: Event) {
	let scrollHandler: (() => void) | null = null;

	PopoverHelper.create({
		props: {
			escToClose: true,
			hideOverlay: true,
			lockScroll: false,
		},
		slotId: 'disclaimer-popover',
		popoverProps: {
			cssClass: 'disclaimer-popover',
			boundingElement: event.currentTarget,
			enableArrow: true,
		},
		component: {
			render: (h: CreateElement, context: RenderContext) => {
				if (props.disclaimerText) {
					return h('div', { class: 'disclaimer-text' }, [h('span', props.disclaimerText)]);
				}

				return h('div', { class: 'disclaimer-text' }, [
					h('span', t('WEBAPP_APPLE_DISCLAIMER_TEXT')),
					h('span', t('WEBAPP_APPLE_DISCLAIMER_SUB_TEXT', { price: props.price }) as string),
				]);
			},
		},
		onOpened: () => {
			// Close popover when user scrolls
			scrollHandler = () => {
				if (import.meta.client) {
					VFMHelper.closeById('disclaimer-popover');
				}
			};
			window.addEventListener('scroll', scrollHandler, { passive: true });
		},
		onClosed: () => {
			// Clean up scroll listener
			if (scrollHandler) {
				window.removeEventListener('scroll', scrollHandler);
				scrollHandler = null;
			}
		},
	});
}
</script>

<style lang="scss">
@import '~balloon-css/src/balloon';
.disclaimer-button {
	background-color: transparent;
	border: none;
	color: #c6c8cd;
	cursor: pointer;
	font-size: 15px;
	padding: 0;
}
.disclaimer-popover {
	background-color: transparent !important;
	padding-top: 10px;
	margin-top: -5px;

	&::after {
		content: '';
		position: absolute;
		background-color: #111924;
		border-left: 1px solid #39424d;
		border-top: 1px solid #39424d;
		left: var(--arrow-left, calc(50% - 5px));
		top: 10px;
		width: 10px;
		height: 10px;
		transform: translate(-5px, -50%) rotate(45deg);
		z-index: 1;
	}

	.disclaimer-text {
		position: relative;
		z-index: 1;
		max-width: 280px;
		width: inherit;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		justify-content: flex-start;
		font-size: 16px;
		color: #c6c8cd;
		gap: 20px;
		border: 1px solid #39424d;
		background: #111924;
		border-radius: 12px;
		padding: 16px;
	}
}
</style>
